import { useState, useMemo } from 'react';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Search, ArrowUpDown } from 'lucide-react';

export function ReportTable({ data }) {
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);

  const rows = useMemo(() => {
    let out = data.rows;
    if (q) {
      const needle = q.toLowerCase();
      out = out.filter(r => data.columns.some(c => String(r[c.key] ?? '').toLowerCase().includes(needle)));
    }
    if (sortKey) {
      out = [...out].sort((a, b) => {
        const av = a[sortKey], bv = b[sortKey];
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sortDir;
        return String(av ?? '').localeCompare(String(bv ?? '')) * sortDir;
      });
    }
    return out;
  }, [data, q, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => -d);
    else { setSortKey(key); setSortDir(1); }
  };

  return (
    <div className="space-y-3" data-testid="report-table">
      {/* Corporate print header — only shows on print */}
      <div className="hidden print:block border-b-2 border-black pb-3 mb-4">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-2xl font-bold">{data.event?.name}</p>
            <p className="text-sm">{data.label} — {data.event?.event_date}{data.event?.venue_name ? ` · ${data.event.venue_name}` : ''}</p>
          </div>
          <div className="text-right text-xs">
            <p className="font-bold tracking-widest">EVENTISM</p>
            <p>Generated {String(data.generated_at || '').slice(0, 16).replace('T', ' ')} UTC</p>
            {(data.date_from || data.date_to) && <p>Range: {data.date_from || '…'} → {data.date_to || '…'}</p>}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {data.summary && Object.keys(data.summary).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="report-summary">
          {Object.entries(data.summary).map(([k, v]) => (
            <Card key={k} className="border-border/60 print:border-black">
              <CardContent className="px-4 py-2.5">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground print:text-black">{k}</p>
                <p className="text-lg font-bold print:text-black">{typeof v === 'number' ? v.toLocaleString() : v}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="relative max-w-xs print:hidden">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder={`Search ${data.rows.length} rows…`} value={q} onChange={e => setQ(e.target.value)} className="h-8 pl-8 text-sm" data-testid="report-search" />
      </div>

      <div className="rounded-lg border border-border overflow-x-auto print:border-black">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 print:bg-gray-200">
              {data.columns.map(c => (
                <th key={c.key} className="text-left px-3 py-2 font-semibold whitespace-nowrap cursor-pointer select-none print:text-black"
                  onClick={() => toggleSort(c.key)} data-testid={`report-col-${c.key}`}>
                  <span className="inline-flex items-center gap-1">{c.label}<ArrowUpDown className="h-3 w-3 opacity-40 print:hidden" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={data.columns.length} className="px-3 py-8 text-center text-muted-foreground">No rows for this report{q ? ' matching your search' : ''}.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-border/60 hover:bg-muted/20 print:border-gray-300">
                {data.columns.map(c => (
                  <td key={c.key} className="px-3 py-1.5 whitespace-nowrap max-w-[280px] overflow-hidden text-ellipsis print:text-black">
                    {String(r[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-muted-foreground print:text-black">{rows.length} of {data.rows.length} rows · Report: {data.label} · Eventism</p>
    </div>
  );
}
